'use client';

import React, { useState } from 'react';
import { useRouter } from 'next/navigation';
import Image from 'next/image';
import { useAuth } from '@/contexts/AuthContext';

const demoAccounts = [
  { label: 'Admin', email: 'admin@mouridoulah.org', password: 'Admin2026!', color: '#c9a84c' },
  { label: 'Finance', email: 'finance@mouridoulah.org', password: 'Finance2026!', color: '#60a5fa' },
  { label: 'Secrétariat', email: 'secretariat@mouridoulah.org', password: 'Secretariat2026!', color: '#34d399' },
];

function getRoleRedirect(role: string): string {
  if (role === 'admin') return '/panneau-admin';
  if (role === 'finance') return '/espace-finance';
  if (role === 'secretariat') return '/espace-secretariat';
  return '/espace-donateur';
}

export default function AdminLoginPage() {
  const router = useRouter();
  const { signIn } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const data = await signIn(email, password);
      const role = data?.user?.user_metadata?.role ?? 'member';
      router.replace(getRoleRedirect(role));
      router.refresh();
    } catch (err: any) {
      setError(err?.message || 'Identifiants incorrects. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4"
      style={{ background: 'linear-gradient(135deg, #1a1208 0%, #2d1f08 50%, #3d2a0a 100%)' }}
    >
      {/* Background pattern */}
      <div className="absolute inset-0 opacity-5 pointer-events-none" style={{
        backgroundImage: 'radial-gradient(circle at 25% 25%, #c9a84c 0%, transparent 50%), radial-gradient(circle at 75% 75%, #c9a84c 0%, transparent 50%)'
      }} />

      <div className="w-full max-w-md relative z-10">
        {/* Logo & Title */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <div className="w-20 h-20 rounded-2xl flex items-center justify-center shadow-2xl overflow-hidden" style={{ background: 'rgba(201,168,76,0.15)', border: '1px solid rgba(201,168,76,0.3)' }}>
              <Image
                src="/assets/images/LOGO_MOURIDOULAH_KHIDMA_Plan_de_travail_1__3_-1787850667375.png"
                alt="Mouridoulah Khidma Logo"
                width={60}
                height={60}
                className="object-contain"
              />
            </div>
          </div>
          <h1 className="text-2xl font-bold mb-1" style={{ color: '#c9a84c' }}>
            Mouridoulah Khidma
          </h1>
          <p className="text-sm" style={{ color: 'rgba(201,168,76,0.6)' }}>
            Plateforme de Gestion — Connexion
          </p>
        </div>

        {/* Login Card */}
        <div className="rounded-2xl p-8 shadow-2xl" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(201,168,76,0.2)', backdropFilter: 'blur(10px)' }}>
          <h2 className="text-lg font-semibold mb-6 text-white">Connexion ERP</h2>

          {error && (
            <div className="mb-4 px-4 py-3 rounded-lg text-sm flex items-center gap-2" style={{ background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.3)', color: '#fca5a5' }}>
              <span>⚠</span>
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: 'rgba(201,168,76,0.8)' }}>
                Adresse e-mail
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="votre@email.org"
                required
                className="w-full px-4 py-3 rounded-xl text-sm outline-none transition-all"
                style={{
                  background: 'rgba(255,255,255,0.08)',
                  border: '1px solid rgba(201,168,76,0.25)',
                  color: '#f5e9c8',
                }}
                onFocus={(e) => (e.target.style.borderColor = 'rgba(201,168,76,0.6)')}
                onBlur={(e) => (e.target.style.borderColor = 'rgba(201,168,76,0.25)')}
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: 'rgba(201,168,76,0.8)' }}>
                Mot de passe
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  className="w-full px-4 py-3 pr-12 rounded-xl text-sm outline-none transition-all"
                  style={{
                    background: 'rgba(255,255,255,0.08)',
                    border: '1px solid rgba(201,168,76,0.25)',
                    color: '#f5e9c8',
                  }}
                  onFocus={(e) => (e.target.style.borderColor = 'rgba(201,168,76,0.6)')}
                  onBlur={(e) => (e.target.style.borderColor = 'rgba(201,168,76,0.25)')}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-sm transition-colors"
                  style={{ color: 'rgba(201,168,76,0.5)' }}
                  onMouseEnter={(e) => ((e.target as HTMLElement).style.color = '#c9a84c')}
                  onMouseLeave={(e) => ((e.target as HTMLElement).style.color = 'rgba(201,168,76,0.5)')}
                >
                  {showPassword ? '🙈' : '👁'}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 rounded-xl text-sm font-semibold transition-all duration-200 flex items-center justify-center gap-2"
              style={{
                background: loading ? 'rgba(201,168,76,0.4)' : 'linear-gradient(135deg, #a07830, #c9a84c)',
                color: loading ? 'rgba(255,255,255,0.5)' : '#1a1208',
                cursor: loading ? 'not-allowed' : 'pointer',
              }}
            >
              {loading ? (
                <>
                  <span className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                  Connexion en cours...
                </>
              ) : (
                'Se connecter'
              )}
            </button>
          </form>

          {/* Demo credentials */}
          <div className="mt-6 pt-5 border-t" style={{ borderColor: 'rgba(201,168,76,0.15)' }}>
            <p className="text-xs font-medium mb-3" style={{ color: 'rgba(201,168,76,0.5)' }}>
              Comptes de démonstration
            </p>
            <div className="space-y-2">
              {demoAccounts.map((account) => (
                <button
                  key={account.label}
                  type="button"
                  onClick={() => { setEmail(account.email); setPassword(account.password); }}
                  className="w-full rounded-lg p-3 text-left transition-all"
                  style={{ background: 'rgba(201,168,76,0.06)', border: '1px solid rgba(201,168,76,0.12)' }}
                  onMouseEnter={e => (e.currentTarget.style.borderColor = 'rgba(201,168,76,0.3)')}
                  onMouseLeave={e => (e.currentTarget.style.borderColor = 'rgba(201,168,76,0.12)')}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-bold px-2 py-0.5 rounded-full" style={{ background: `${account.color}20`, color: account.color }}>
                      {account.label}
                    </span>
                    <span className="text-xs font-mono" style={{ color: 'rgba(255,255,255,0.4)' }}>cliquer pour remplir</span>
                  </div>
                  <p className="text-xs font-mono mt-1.5" style={{ color: 'rgba(255,255,255,0.6)' }}>{account.email}</p>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Donor portal link */}
        <p className="text-center mt-6 text-sm" style={{ color: 'rgba(201,168,76,0.4)' }}>
          Vous êtes donateur ?{' '}
          <a href="/espace-donateur/login" style={{ color: 'rgba(201,168,76,0.7)' }} className="hover:underline">
            Accéder à l&apos;espace donateur
          </a>
        </p>
      </div>
    </div>
  );
}
